'use client';

import { useEffect, useState } from 'react';
import { useBreakpointValue, Box, Flex, Text, Button, VStack } from '@skin-seoul/design-system';
import { useCallbackRef } from '@skin-seoul/utils';
import { useReviewService } from '@skin-seoul/service';
import type { ProductDetail, Review as ServiceReview } from '@skin-seoul/service';
import ReviewCard from './components/ReviewCard';
import ReviewFilterBar, { sortOptions } from './components/ReviewFilterBar';
import ReviewHeader from './components/ReviewHeader';
import LoadMoreButton from './components/LoadMoreButton';
import type { ReviewFormData } from './components/common/interfaces';
import { ReviewWriteMobileModal } from './components/ReviewWriteMobileModal';
import { ReviewWritePcModal } from './components/ReviewWritePcModal';
import { ReviewDetailModal } from './components/ReviewDetailModal';
import { cleanSkinData } from './utils';
import type { ComponentProps } from 'react';
import ReviewImageGallery from './components/ReviewImageGallery';
import ReviewEmpty from './components/ReviewEmpty';

// 더미 리뷰 데이터
//TODO: remove dummy reviews
const dummyReviews: Review[] = [
  {
    id: 'dummy-review-1',
    rating: 5,
    title: 'Amazing Results! My Skin Has Never Felt Better',
    content:
      'I was amazed by the variety of skincare products available on this site. From well-known brands to hidden gems, they have it all. The guided filter option made it so more o find the right products for my skin type. Checkout was smooth, and the delivery was on time. Definitely my go-to skincare store!',
    author: {
      id: 'user-1',
      name: 'Aisha P.',
      avatar: '',
    },
    verifiedPurchase: true,
    createdAt: '2024-02-11T10:30:00Z',
    likeCount: 24,
    skinType: ['Oily Skin', 'Acne'],
    skinConcern: ['Dryness'],
    images: [
      {
        id: 'dummy-review-1_0',
        url: 'https://placehold.co/1400x1000/FFB6C1/FFFFFF?text=Before',
        alt: 'Before using product',
        reviewId: 'dummy-review-1',
      },
      {
        id: 'dummy-review-1_1',
        url: 'https://placehold.co/279x366/98FB98/FFFFFF?text=After',
        alt: 'After using product',
        reviewId: 'dummy-review-1',
      },
    ],
  },
  {
    id: 'dummy-review-2',
    rating: 4,
    title: 'Great Product, Visible Improvement',
    content:
      'This serum has been a game-changer for my skincare routine. I noticed visible improvements in my skin texture within just two weeks of use. The packaging is elegant and the product feels luxurious. Highly recommend for anyone looking to upgrade their skincare game!',
    author: {
      id: 'user-2',
      name: 'Sarah M.',
      avatar: '',
    },
    verifiedPurchase: true,
    createdAt: '2024-02-10T14:22:00Z',
    likeCount: 18,
    skinType: ['Dry Skin'],
    skinConcern: ['Fine Lines', 'Dullness'],
    images: [
      {
        id: 'dummy-review-2_0',
        url: 'https://placehold.co/279x366/DDA0DD/FFFFFF?text=Product',
        alt: 'Product photo',
        reviewId: 'dummy-review-2',
      },
    ],
  },
  {
    id: 'dummy-review-3',
    rating: 5,
    title: 'Perfect for Sensitive Skin',
    content:
      'As someone with very sensitive skin, I was hesitant to try new products. But this gentle cleanser has been perfect! No irritation, no breakouts, just clean and soft skin. The formula is so gentle yet effective.',
    author: {
      id: 'user-3',
      name: 'Emma L.',
      avatar: '',
    },
    verifiedPurchase: true,
    createdAt: '2024-02-09T09:15:00Z',
    likeCount: 31,
    skinType: ['Sensitive Skin'],
    skinConcern: ['Redness', 'Irritation'],
    images: [
      {
        id: 'dummy-review-3_0',
        url: 'https://placehold.co/279x366/F0E68C/FFFFFF?text=Routine',
        alt: 'My skincare routine',
        reviewId: 'dummy-review-3',
      },
      {
        id: 'dummy-review-3_1',
        url: 'https://placehold.co/279x366/87CEEB/FFFFFF?text=Results',
        alt: 'Skin results',
        reviewId: 'dummy-review-3',
      },
      {
        id: 'dummy-review-3_2',
        url: 'https://placehold.co/279x366/F4A460/FFFFFF?text=Texture',
        alt: 'Product texture',
        reviewId: 'dummy-review-3',
      },
    ],
  },
  {
    id: 'dummy-review-4',
    rating: 3,
    title: 'Good but Not Amazing',
    content:
      "The product works as described, but I was expecting more dramatic results. It's a decent moisturizer that does the job, but nothing extraordinary. The price point is fair for what you get.",
    author: {
      id: 'user-4',
      name: 'Jessica K.',
      avatar: '',
    },
    verifiedPurchase: true,
    createdAt: '2024-02-08T16:45:00Z',
    likeCount: 7,
    skinType: ['Combination Skin'],
    skinConcern: ['Dryness'],
    images: [],
  },
  {
    id: 'dummy-review-5',
    rating: 5,
    title: 'Holy Grail Product!',
    content:
      "This is hands down the best skincare product I've ever used! My skin looks brighter, feels smoother, and the fine lines around my eyes have visibly reduced. I've already ordered three more bottles. Worth every penny!",
    author: {
      id: 'user-5',
      name: 'Maria G.',
      avatar: '',
    },
    verifiedPurchase: true,
    createdAt: '2024-02-07T11:20:00Z',
    likeCount: 42,
    skinType: ['Mature Skin'],
    skinConcern: ['Fine Lines', 'Dark Spots', 'Dullness'],
    images: [
      {
        id: 'dummy-review-5_0',
        url: 'https://placehold.co/279x366/FFE4E1/FFFFFF?text=Before+%26+After',
        alt: 'Before and after comparison',
        reviewId: 'dummy-review-5',
      },
      {
        id: 'dummy-review-5_1',
        url: 'https://placehold.co/279x366/E6E6FA/FFFFFF?text=Close+Up',
        alt: 'Close up of skin improvement',
        reviewId: 'dummy-review-5',
      },
    ],
  },
];

const LOAD_MORE_REVIEW_LIMIT_COUNT = 8;

type BoxProps = ComponentProps<typeof Box>;

const Container = ({ children, ...props }: { children: React.ReactNode } & Omit<BoxProps, 'children'>) => (
  <Box
    w="full"
    bg="#ffffff"
    fontFamily="Plus Jakarta Sans, sans-serif"
    boxSizing="border-box"
    maxW={{ base: 'full', tablet: '1280px' }}
    mx={{ base: '0', tablet: 'auto' }}
    {...props}
  >
    {children}
  </Box>
);

const Divider = ({ ...props }: BoxProps) => (
  <Box border="none" h="1px" bg="#e2e2e2" my={{ base: '24px', tablet: '32px' }} {...props} />
);

export interface ReviewImage {
  id: string;
  url: string;
  alt: string;
  reviewId: string;
}

export interface Review {
  id: string;
  rating: number;
  title: string;
  content: string;
  author: {
    id: string;
    name: string;
    avatar?: string;
  };
  verifiedPurchase: boolean;
  createdAt: string;
  likeCount: number;
  skinType: string[];
  skinConcern: string[];
  images?: ReviewImage[];
}

function mapServiceReviewToReview(r: ServiceReview): Review {
  return {
    id: String(r.reviewId),
    rating: r.rating,
    title: r.title,
    content: r.body,
    author: {
      id: '',
      name: r.name,
      avatar: '',
    },
    verifiedPurchase: true,
    createdAt: r.modifiedAt,
    likeCount: 0,
    skinType: cleanSkinData(r.skinType),
    skinConcern: cleanSkinData(r.skinConcern),
    images: r.pictureUrls.map((url, idx) => ({
      id: `${r.reviewId}_${idx}`,
      url,
      alt: r.title,
      reviewId: String(r.reviewId),
    })),
  };
}

interface ReviewSectionProps {
  product: ProductDetail;
}

export const ReviewSection = ({ product }: ReviewSectionProps) => {
  const [displayedReviewsCount, setDisplayedReviewsCount] = useState(LOAD_MORE_REVIEW_LIMIT_COUNT);
  const [isPcWriteModalOpen, setIsPcWriteModalOpen] = useState(false);
  const [isMobileWriteOpen, setIsMobileWriteOpen] = useState(false);
  const [isImageGalleryOpen, setIsImageGalleryOpen] = useState(false);
  const [imageGalleryInitialState, setImageGalleryInitialState] = useState<{
    selectedReview: Review | null;
    selectedImageId: string | null;
  }>({
    selectedReview: null,
    selectedImageId: null,
  });

  const [sort, setSort] = useState<string>(sortOptions.at(0)?.value || '');
  const [skinTypes, setSkinTypes] = useState<string[]>([]);
  const [skinConcerns, setSkinConcerns] = useState<string[]>([]);
  const isPC = useBreakpointValue({ base: false, tablet: true });
  const [isCollapsed, setIsCollapsed] = useState(false);

  const reviewService = useReviewService();
  const { useGetProductReviews, useCreateReview } = reviewService;

  const { mutate: createReview, isPending: isCreatingReview } = useCreateReview();

  const { data: reviewsRaw = [], isLoading: isReviewsLoading } = useGetProductReviews(Number(product.productId));
  const serverReviews = reviewsRaw.map(mapServiceReviewToReview);

  //TODO: remove dummy images
  const reviews = [...serverReviews, ...dummyReviews];

  const serverImages = reviewsRaw.flatMap((r) =>
    r.pictureUrls.map((url, idx) => ({
      id: `${r.reviewId}_${idx}`,
      url,
      alt: r.title,
      reviewId: String(r.reviewId),
    })),
  );
  //TODO: remove dummy images
  const dummyImages = dummyReviews.flatMap((review) => review.images || []);

  const customerImages = [...serverImages, ...dummyImages];

  const baseReviews = reviews || [];

  const sortedReviews = (() => {
    switch (sort) {
      case 'newest':
        return [...baseReviews].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      case 'oldest':
        return [...baseReviews].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      case 'highest':
        return [...baseReviews].sort((a, b) => b.rating - a.rating);
      case 'lowest':
        return [...baseReviews].sort((a, b) => a.rating - b.rating);
      default:
        return baseReviews;
    }
  })();

  const sortedAndFilteredReviews = sortedReviews
    .filter((review) => skinTypes.length === 0 || skinTypes.some((type) => review.skinType.includes(type)))
    .filter(
      (review) => skinConcerns.length === 0 || skinConcerns.some((concern) => review.skinConcern.includes(concern)),
    );

  const totalReviewsLength = reviews.length;

  // Load more functionality
  const displayedReviews = sortedAndFilteredReviews.slice(0, displayedReviewsCount);

  const hasMoreReviews = displayedReviewsCount < sortedAndFilteredReviews.length;

  const handleLoadMore = useCallbackRef(() => {
    setDisplayedReviewsCount((prev) => prev + LOAD_MORE_REVIEW_LIMIT_COUNT);
  });

  // Reset displayed count when filters change
  useEffect(() => {
    setDisplayedReviewsCount(LOAD_MORE_REVIEW_LIMIT_COUNT);
  }, [sort, skinTypes, skinConcerns]);

  const averageRating =
    totalReviewsLength === 0
      ? 0
      : Number((reviews.reduce((sum, r) => sum + r.rating, 0) / totalReviewsLength).toFixed(1));

  const ratingDistribution: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  reviews.forEach((r) => {
    const rating = Math.round(r.rating);
    if (rating >= 1 && rating <= 5) {
      ratingDistribution[rating] += 1;
    }
  });

  const hasReviews = reviews.length > 0;

  useEffect(() => {
    if (!isPC && isMobileWriteOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileWriteOpen, isPC]);

  const handleOpenWriteModal = () => {
    if (isPC) {
      setIsPcWriteModalOpen(true);
    } else {
      setIsMobileWriteOpen(true);
    }
  };

  const handleCloseWriteModal = useCallbackRef(() => setIsPcWriteModalOpen(false));

  const handleCloseMobileWriteModal = useCallbackRef(() => setIsMobileWriteOpen(false));

  const handleSubmit = useCallbackRef((values: ReviewFormData) => {
    return new Promise<void>((resolve, reject) => {
      createReview(
        {
          name: values.nickname,
          rating: values.rating,
          body: values.content,
          productId: Number(product.productId),
          title: values.title,
          files: values.images,
          skinType: [values.skinType],
          skinConcern: [values.skinConcern],
          email: values.email,
        },
        {
          onSuccess: () => {
            resolve();
          },
          onError: (error) => {
            reject(new Error('Failed to create review: ' + (error?.message || 'Unknown error')));
          },
        },
      );
    });
  });

  const handleSortChange = (sortBy: string) => {
    setSort(sortBy);
  };

  const handleSkinTypeChange = (skinTypes: string[]) => {
    setSkinTypes(skinTypes);
  };

  const handleSkinConcernChange = (skinConcerns: string[]) => {
    setSkinConcerns(skinConcerns);
  };

  const handleImageClick = useCallbackRef((image: ReviewImage, _index: number) => {
    // 해당 리뷰의 모든 이미지를 찾기
    const review = reviews.find((r) => r.images?.some((img) => img.id === image.id));
    if (review) {
      // ImageGalleryModal을 열고 해당 리뷰와 이미지를 선택된 상태로 설정
      setIsImageGalleryOpen(true);
      // 초기 선택된 리뷰와 이미지 정보를 저장하기 위한 상태 업데이트
      setImageGalleryInitialState({
        selectedReview: review,
        selectedImageId: image.id,
      });
    }
  });

  const handleViewAllImages = useCallbackRef(() => {
    setIsImageGalleryOpen(true);
  });

  const handleCloseImageGallery = useCallbackRef(() => {
    setIsImageGalleryOpen(false);
    // 초기 상태 리셋
    setImageGalleryInitialState({
      selectedReview: null,
      selectedImageId: null,
    });
  });

  const handleViewMore = useCallbackRef((review: Review) => {
    // ImageGalleryModal을 열고 해당 리뷰를 선택된 상태로 설정
    setIsImageGalleryOpen(true);
    // 리뷰에 이미지가 있으면 첫 번째 이미지를 선택, 없으면 null
    const firstImageId = review.images && review.images.length > 0 ? review.images[0].id : null;
    setImageGalleryInitialState({
      selectedReview: review,
      selectedImageId: firstImageId,
    });
  });

  return (
    <Container id="judgeme_product_reviews">
      <Flex
        align="center"
        justify="space-between"
        cursor="pointer"
        onClick={() => setIsCollapsed(!isCollapsed)}
        mb={{
          base: 6,
          tablet: 4,
        }}
      >
        <Text
          textStyle={{ base: 'label.large', tablet: 'heading.h6' }}
          fontWeight="medium"
          color="neutral.800"
          textAlign="left"
          whiteSpace="nowrap"
        >
          Ratings & Reviews
        </Text>
      </Flex>
      {hasReviews ? (
        <ReviewHeader
          averageRating={averageRating}
          totalReviews={totalReviewsLength}
          ratingDistribution={ratingDistribution}
          customerImages={customerImages}
          reviews={reviews}
          onViewAllImages={handleViewAllImages}
          onWriteReview={handleOpenWriteModal}
          isWriteReviewDisabled={isCreatingReview}
        />
      ) : (
        <ReviewEmpty />
      )}
      <Flex
        w="full"
        align="center"
        justify="center"
        mt={{
          base: '24px',
          tablet: '32px',
        }}
      >
        <Button
          onClick={handleOpenWriteModal}
          disabled={isCreatingReview}
          bg="primary.600"
          h={12}
          w={{
            base: 'full',
            tablet: '362px',
          }}
        >
          Write a review
        </Button>
      </Flex>
      {hasReviews && !isReviewsLoading && (
        <>
          <Divider />
          <ReviewImageGallery images={customerImages} onViewAll={handleViewAllImages} />
          <VStack
            align="start"
            gap={3}
            mt={{
              base: 6,
              tablet: 8,
            }}
          >
            <Text
              textStyle={{
                base: 'label.medium',
                tablet: 'label.large',
              }}
              fontWeight="medium"
            >
              Filters
            </Text>
            <ReviewFilterBar
              onSortChange={handleSortChange}
              onSkinTypeChange={handleSkinTypeChange}
              onSkinConcernChange={handleSkinConcernChange}
            />
          </VStack>
          <VStack
            gap="14px"
            align="start"
            mt={{
              base: 6,
              tablet: 8,
            }}
          >
            <Text
              textStyle={{
                base: 'label.medium',
                tablet: 'heading.h6',
              }}
              fontWeight="medium"
            >
              Reviews
            </Text>
            <VStack gap="16px" alignSelf="start">
              {displayedReviews.map((review, columnIndex, columnArray) => (
                <ReviewCard
                  key={review.id}
                  review={review}
                  onImageClick={handleImageClick}
                  onViewMore={handleViewMore}
                  isLastInColumn={columnIndex === columnArray.length - 1}
                />
              ))}
            </VStack>
          </VStack>
          {hasMoreReviews && (
            <Flex
              align="center"
              justify="center"
              w="full"
              mt={{
                base: 6,
                tablet: 8,
              }}
            >
              <LoadMoreButton onClick={handleLoadMore} />
            </Flex>
          )}
        </>
      )}
      {isPC && (
        <ReviewWritePcModal
          isOpen={isPcWriteModalOpen}
          productInfo={{
            name: product.name || '',
            brand: product.brand || '',
            image: product.productImageUrls[0] || '',
            category: product.category[0] || '',
          }}
          onClose={handleCloseWriteModal}
          onSubmit={handleSubmit}
        />
      )}
      {!isPC && (
        <ReviewWriteMobileModal
          isOpen={isMobileWriteOpen}
          productInfo={{
            name: product.name || '',
            brand: product.brand || '',
            image: product.productImageUrls[0] || '',
            category: product.category[0] || '',
          }}
          onClose={handleCloseMobileWriteModal}
          onSubmit={handleSubmit}
        />
      )}
      <ReviewDetailModal
        isOpen={isImageGalleryOpen}
        onClose={handleCloseImageGallery}
        images={customerImages}
        reviews={reviews}
        initialSelectedReview={imageGalleryInitialState.selectedReview}
        initialSelectedImageId={imageGalleryInitialState.selectedImageId}
      />
    </Container>
  );
};

export default ReviewSection;
